import { isConnectionFailure } from "../loginOffline/connectionDiagnosis";

/**
 * Why GET /api/users/me ended without a user. Only `notRegistered` means the
 * account is unknown to LiftCloud; the others are the request failing, and
 * telling the customer "not registered" for them sends support after an
 * account that is fine.
 */
export type CurrentUserFailureCause =
  | "notRegistered" // LiftCloud answered 403: no users row for this sign-in
  | "unreachable" // no reply reached the page (network, CORS, proxy, timeout)
  | "unreadableReply" // a reply arrived but could not be decoded (PARSING_ERROR)
  | "other"; // anything else: another status, or a reply without a user

/** The `code` LiftCloud's 403 carries when no users row matches the sign-in. */
export const NOT_REGISTERED_CODE = "not_registered";

/** The message of that 403, which LiftCloud sent before the code existed. */
export const NOT_REGISTERED_MESSAGE = "Not registered as a Liftnet user";

export type CurrentUserFailure = {
  cause: CurrentUserFailureCause;
  /** Quoted to support: the server's request_id when it sent one, else made here. */
  reference: string;
  failedAt: Date;
  /** The RTK Query error, or undefined when the reply simply held no user. */
  error: unknown;
};

const bodyOf = (error: unknown): Record<string, unknown> | undefined => {
  if (typeof error !== "object" || error === null) return undefined;
  const { data } = error as { data?: unknown };
  return typeof data === "object" && data !== null ? (data as Record<string, unknown>) : undefined;
};

const statusOf = (error: unknown): unknown =>
  typeof error === "object" && error !== null ? (error as { status?: unknown }).status : undefined;

/**
 * True for LiftCloud's "not registered" 403: its `code`, or, from a LiftCloud
 * that predates the code, its message. Any other 403 (a rejected token) is not.
 */
export const isNotRegisteredReply = (error: unknown): boolean => {
  if (statusOf(error) !== 403) return false;
  const body = bodyOf(error);
  if (!body) return false;
  return (
    body.code === NOT_REGISTERED_CODE ||
    body.message === NOT_REGISTERED_MESSAGE ||
    body.error === NOT_REGISTERED_MESSAGE
  );
};

export const classifyCurrentUserFailure = (error: unknown): CurrentUserFailureCause => {
  if (isNotRegisteredReply(error)) return "notRegistered";
  if (isConnectionFailure(error)) return "unreachable";
  if (statusOf(error) === "PARSING_ERROR") return "unreadableReply";
  return "other";
};

const REFERENCE_PATTERN = /^[A-Za-z0-9-]{4,64}$/;

/** A fresh 8-character reference, for a failure the server gave none for. */
export const newReference = (): string =>
  Math.random().toString(36).slice(2, 10).toUpperCase().padEnd(8, "0");

/**
 * The reference shown to the customer: the server's request_id when its reply
 * carried a well-formed one (it is also in LiftCloud's log line for the
 * refusal), else a fresh one.
 */
export const referenceFor = (error: unknown): string => {
  const requestId = bodyOf(error)?.request_id;
  return typeof requestId === "string" && REFERENCE_PATTERN.test(requestId)
    ? requestId
    : newReference();
};

export const describeCurrentUserFailure = (
  error: unknown,
  failedAt: Date = new Date(),
): CurrentUserFailure => ({
  cause: error === undefined ? "other" : classifyCurrentUserFailure(error),
  reference: referenceFor(error),
  failedAt,
  error,
});

/** The i18n keys of the one title and one hint the customer sees for a cause. */
export const CURRENT_USER_FAILURE_TEXT: Record<
  CurrentUserFailureCause,
  { title: string; hint: string }
> = {
  notRegistered: {
    title: "authentication.userNotRegisteredTitle",
    hint: "authentication.userNotRegisteredHint",
  },
  unreachable: {
    title: "authentication.serverUnreachableTitle",
    hint: "authentication.accountNotLoadedHint",
  },
  unreadableReply: {
    title: "authentication.serverReplyUnreadableTitle",
    hint: "authentication.accountNotLoadedHint",
  },
  other: {
    title: "authentication.accountNotLoadedTitle",
    hint: "authentication.accountNotLoadedHint",
  },
};
